import { BreadcrumbBox } from '@/features/Breadcrumb';
import { SEO } from '@/features/SEO';
import {
  ContentContainer,
  ImageCarousel,
  RelatedLinks,
  SocialShare,
  TwoColumnContent,
} from '@/features/shared';
import { MuiContainer } from '@/features/shared/material-ui';
import ModalContent from '@/features/shared/ModalContent';
import { ColorNames, SpacingSizes, Theme } from '@/utils/constants';
import { getDocumentBasedImage } from '@/utils/helpers';
import resolveTheme from '@/utils/resolvers/resolveTheme';
import clsx from 'clsx';
import React, { memo, useMemo, useState } from 'react';
import { AdditionalContent } from './AdditionalContent';
import { HeaderContent } from './HeaderContent';
import { MainContent } from './MainContent';
import { PressReleaseProps } from './PressRelease.interface';
import useStyles from './PressRelease.styles';
import { MediaContacts } from './shared';

const PressRelease = ({
  title,
  shortDescription,
  publicationDate,
  location,
  pdf,
  images,
  relatedLinks,
  mediaContacts,
  includeBreadcrumb,
  includeDownloadOpt,
  legacyContent,
  detailContent,
  displaySocialShare,
  descriptiveContent,
  pageMetadata,
  contentButtons,
  theme = Theme.dark,
  backgroundColor = ColorNames.eggshell,
  labels,
  downloadIconPath,
  cardImage = {},
  tags,
  contentCategory,
}: Br.PropsWithContentButtons<PressReleaseProps>) => {
  const classes = useStyles({});
  const bgBreadcrumbTheme = resolveTheme('bg-color')({ rawVal: backgroundColor });

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(0);

  const headerContentProps = useMemo(
    () => ({
      title,
      shortDescription,
      publicationDate: publicationDate?.formatted,
      location,
      pdf: includeDownloadOpt && pdf?.url ? pdf : undefined,
      theme,
      downloadIcon: downloadIconPath,
    }),
    [
      downloadIconPath,
      includeDownloadOpt,
      location,
      pdf,
      publicationDate,
      shortDescription,
      theme,
      title,
    ],
  );

  const galleryContent = useMemo(
    () => ({
      contentList: images?.map((image) => ({ image })),
    }),
    [images],
  );

  const mediaContactsProps = {
    mediaContacts,
    theme,
  };

  const relatedLinksProps = {
    content: relatedLinks,
    theme,
  };

  const hasRelatedLinks = React.useMemo(() => Boolean(relatedLinks), [relatedLinks]);

  const mainContentProps = {
    legacyContent,
    detailContent,
    theme,
  };

  const additionalContentProps = {
    content: descriptiveContent,
    theme,
  };

  const metadata = {
    ...pageMetadata,
    metaTitle: pageMetadata?.metaTitle || title,
    description: pageMetadata?.description || shortDescription,
    socialMediaImage: getDocumentBasedImage({ pageMetadata, cardImage }),
  };

  const handleSyncNav = (currentIndex: number, nextIndex: number) => {
    setValue(nextIndex);
  };

  const handleToggleModal = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <article className={classes.root} data-testid="press-release--container">
      <ContentContainer
        topSpacing={SpacingSizes.small}
        bottomSpacing={SpacingSizes.medium}
        topBackgroundColor={backgroundColor}
        bottomBackgroundColor={backgroundColor}
      >
        <MuiContainer>
          <div className={classes.content}>
            {includeBreadcrumb && (
              <BreadcrumbBox className={classes.breadcrumb} theme={bgBreadcrumbTheme} />
            )}

            <TwoColumnContent
              columnRatio="70-30"
              className={classes.columns}
              leftColumn={
                <>
                  <header data-datalayer-position="header_content">
                    {contentButtons?.content}
                    <HeaderContent {...headerContentProps} />
                  </header>

                  <section>
                    <MainContent {...mainContentProps} />
                    {displaySocialShare && (
                      <SocialShare
                        theme={theme}
                        shareText={labels?.share_text}
                        title={pageMetadata?.metaTitle || title}
                      />
                    )}
                  </section>

                  <footer>
                    <AdditionalContent {...additionalContentProps} />
                  </footer>
                </>
              }
              rightColumn={
                <>
                  <ImageCarousel
                    content={galleryContent}
                    index={value}
                    syncNav={handleSyncNav}
                    theme={theme}
                    inline
                    size="cover"
                    pagination={false}
                    click={handleToggleModal}
                    className={clsx(classes.carousel, `bg-color-${backgroundColor}`)}
                    type="card"
                    labels={labels}
                  />
                  <ModalContent isOpen={isOpen} toggleOpen={handleToggleModal}>
                    <MuiContainer className="medium">
                      <ImageCarousel
                        content={galleryContent}
                        index={value}
                        theme={Theme.dark}
                        type="background"
                        labels={labels}
                        size="contain"
                      />
                    </MuiContainer>
                  </ModalContent>
                  {hasRelatedLinks && <RelatedLinks {...relatedLinksProps} />}
                  {mediaContacts && mediaContacts.length > 0 && (
                    <MediaContacts {...mediaContactsProps} />
                  )}
                </>
              }
            />
          </div>
        </MuiContainer>
      </ContentContainer>
      <SEO
        metadata={metadata}
        tags={tags}
        contentCategory={contentCategory}
        publicationDate={publicationDate?.iso}
        isDocumentBackedPage
      />
    </article>
  );
};

export default memo(PressRelease);
